import { RiskConfig } from '@Types/risk.types';
import { AssetIdCell } from './RenderCell/AssetIdCell';
import { AttributeCell } from './RenderCell/AttributeCell';
import { ConditionCell } from './RenderCell/ConditionCell';
import { CctvInspectedCell } from './RenderCell/CctvInspectedCell';
import { LastInspectedCell } from './RenderCell/LastInspectedCell';
import { DataCoverageCell } from './RenderCell/DataCoverageCell';
import {
	COVERAGE_THRESHOLDS,
	coverageBand,
	coverageBandKey,
} from './RenderCell/coverageBand';
import { DiameterCell } from './RenderCell/DiameterCell';
import { GradeBadgeCell } from './RenderCell/GradeBadgeCell';
import { MenuCell } from './RenderCell/MenuCell';
import { StatusCell } from './RenderCell/StatusCell';
import { TrendCell } from './RenderCell/TrendCell';
import {
	GridColDef,
	GridFilterOperator,
	getGridNumericOperators,
	getGridSingleSelectOperators,
	getGridStringOperators,
	useGlobalization,
} from '@innovyze/stylovyze';
import React from 'react';
import useGetWeightedScores from './useGetWeightedScores';
import { useResultsGridFlags } from './useResultsGridFlags';
import { useNoConditionData } from '@Hooks/useNoConditionData';

/* eslint-disable react/display-name */

// Stored unit per column: the unit the risk API compares filters in (the
// schema unit). Used to convert filter values typed in the tenant's unit system.
export const FIELD_UNITS: Record<string, string> = { DIAMETER: 'mm' };

// The grid row's GRADE is undefined when the asset has no risk result
// (mapRowToCells), the same assets the KPI summary counts as unscored.
const isUnscoredRow = (row: { GRADE?: unknown }) => row.GRADE == null;

const isOperatorOnly = (operator: GridFilterOperator) =>
	operator.value === 'is';

// Redesign minimum widths, sized to each header so the default columns fit
// beside the pinned Menu and explanation columns on a laptop-width grid
// (flex still shares out any spare width).
const REDESIGN_MIN_WIDTHS: Record<string, number> = {
	ASSET_ID: 140,
	MATERIAL: 85,
	DIAMETER: 90,
	INSTALLATION_YEAR: 85,
	CURRENT_LOF: 60,
	CURRENT_COF: 60,
	CURRENT_RISK: 110,
	GRADE: 120,
	RISK_CHANGE: 65,
	CCTV_INSPECTED: 120,
	LAST_INSPECTED: 120,
	DATA_COVERAGE: 120,
};

const withRedesignWidth = (column: GridColDef): GridColDef => {
	const minWidth = REDESIGN_MIN_WIDTHS[column.field];
	if (minWidth === undefined) return column;
	return column.width === undefined
		? { ...column, minWidth }
		: { ...column, minWidth, width: minWidth };
};

export function useRiskDetailsColumns(riskConfig: RiskConfig): GridColDef[] {
	const { t } = useGlobalization();
	const weightedScores = useGetWeightedScores();
	const { isRedesign, showDriverColumns } = useResultsGridFlags();

	const formatTrendValue = (value: unknown): string => {
		if (value == null) return '';

		const numValue = Number(value);
		return isNaN(numValue) ? String(value) || '' : numValue.toFixed(3);
	};

	const columns: GridColDef[] = [
		{
			field: 'ASSET_ID',
			headerName: t('Asset ID'),
			align: 'left',
			renderCell: params => (
				<AssetIdCell assetId={params.value as string} />
			),
			minWidth: 150,
			flex: 1,
			sortable: true,
			filterable: false,
			hideable: false,
		},
		{
			field: 'RISK_CHANGE',
			align: 'left',
			headerName: isRedesign ? t('Trend') : t('Risk Trend'),
			renderCell: params => {
				return (
					<TrendCell
						trend={formatTrendValue(params.value)}
						redesign={isRedesign}
					/>
				);
			},
			minWidth: 150,
			flex: 1,
			sortable: true,
			filterable: false,
		},
		{
			field: 'CURRENT_RISK',
			align: 'left',
			headerName: t('Risk Score'),
			valueFormatter: params => {
				formatTrendValue(params.value);
			},
			minWidth: 150,
			flex: 1,
			sortable: true,
			filterable: false,
		},
		{
			field: 'CURRENT_LOF',
			align: 'left',
			headerName: isRedesign ? t('LoF') : t('Total LoF'),
			valueFormatter: params => {
				formatTrendValue(params.value);
			},
			minWidth: 150,
			flex: 1,
			sortable: true,
			filterable: false,
		},
		{
			field: 'CURRENT_COF',
			align: 'left',
			headerName: isRedesign ? t('CoF') : t('Total CoF'),
			valueFormatter: params => {
				formatTrendValue(params.value);
			},
			minWidth: 150,
			flex: 1,
			sortable: true,
			filterable: false,
		},
	];
	const noConditionData = useNoConditionData(
		riskConfig.riskSetting.assetType,
	);

	if (!isRedesign && !noConditionData) {
		columns.push({
			field: 'CONDITION_DATA',
			align: 'left',
			headerName: t('Condition Data'),
			renderCell: params => (
				<ConditionCell condition={params.value as string} />
			),
			minWidth: 150,
			flex: 1,
			sortable: false,
			type: 'singleSelect',
			valueOptions: [
				{
					label: t('No'),
					value: '0',
				},
				{
					label: t('Yes'),
					value: '1',
				},
			],
			// Already inside the !noConditionData guard
			filterable: true,
			filterOperators: getGridSingleSelectOperators().filter(
				(operator: GridFilterOperator) => {
					return operator.value == 'is';
				},
			),
		});
	}

	if (isRedesign) {
		const numericOps = (allowed: string[]) =>
			getGridNumericOperators().filter((o: GridFilterOperator) =>
				allowed.includes(o.value),
			);
		if (!noConditionData) {
			columns.push(
				{
					field: 'CCTV_INSPECTED',
					align: 'left',
					headerName: t('CCTV Inspected'),
					renderCell: params => (
						<CctvInspectedCell
							value={params.row.CCTV_INSPECTED}
							rerunNeeded={
								params.row.INSPECTION_RERUN_NEEDED &&
								!isUnscoredRow(params.row)
							}
						/>
					),
					minWidth: 150,
					flex: 1,
					sortable: true,
					filterable: true,
					type: 'singleSelect',
					valueOptions: [
						{ label: t('Inspected'), value: 'true' },
						{ label: t('Not inspected'), value: 'false' },
					],
					valueGetter: params =>
						params.row.CCTV_INSPECTED == null
							? null
							: String(params.row.CCTV_INSPECTED),
					filterOperators:
						getGridSingleSelectOperators().filter(isOperatorOnly),
				},
				{
					field: 'LAST_INSPECTED',
					align: 'left',
					headerName: t('Last inspected'),
					renderCell: params => (
						<LastInspectedCell
							value={params.row.LAST_INSPECTED}
							rerunNeeded={
								params.row.INSPECTION_RERUN_NEEDED &&
								!isUnscoredRow(params.row)
							}
						/>
					),
					minWidth: 140,
					flex: 1,
					sortable: true,
					filterable: false,
				},
			);
		}
		columns.push({
			field: 'DATA_COVERAGE',
			align: 'left',
			headerName: t('Data Coverage'),
			description: t(
				'Share of inputs for this pipe that came from its own data rather than default values. Expand a row to see which components used a default. Reflects the last run, not the current data.',
			),
			// Band key so the value matches the singleSelect options; sorting is server-side.
			valueGetter: params =>
				params.row.DATA_COVERAGE
					? coverageBandKey(params.row.DATA_COVERAGE.pct)
					: null,
			// Unscored assets have no coverage and a re-run won't add it, so no hint.
			renderCell: params =>
				!params.row.DATA_COVERAGE && isUnscoredRow(params.row) ? (
					<AttributeCell value={null} />
				) : (
					<DataCoverageCell
						coverage={params.row.DATA_COVERAGE}
						showRerunHint
					/>
				),
			minWidth: 140,
			flex: 1,
			sortable: true,
			filterable: true,
			type: 'singleSelect',
			valueOptions: [
				{
					label: coverageBand(COVERAGE_THRESHOLDS.complete, t).label,
					value: 'complete',
				},
				{
					label: coverageBand(COVERAGE_THRESHOLDS.partial, t).label,
					value: 'partial',
				},
				{ label: coverageBand(0, t).label, value: 'minimal' },
			],
			filterOperators:
				getGridSingleSelectOperators().filter(isOperatorOnly),
		});
		columns.push(
			{
				field: 'MATERIAL',
				align: 'left',
				headerName: t('Material'),
				renderCell: params => <AttributeCell value={params.value} />,
				minWidth: 140,
				flex: 1,
				sortable: true,
				filterable: true,
				type: 'string',
				filterOperators: getGridStringOperators().filter(
					(o: GridFilterOperator) => o.value === 'equals',
				),
			},
			{
				field: 'DIAMETER',
				align: 'left',
				headerAlign: 'left',
				headerName: t('Diameter'),
				valueGetter: params => params.row.DIAMETER?.value ?? null,
				renderCell: params => (
					<DiameterCell diameter={params.row.DIAMETER} />
				),
				minWidth: 120,
				flex: 1,
				sortable: true,
				filterable: true,
				type: 'number',
				// Backend accepts gte/lt; "=" can't match a converted imperial value.
				filterOperators: numericOps(['>=', '<']),
			},
			{
				field: 'INSTALLATION_YEAR',
				align: 'left',
				headerAlign: 'left',
				headerName: t('Installed'),
				renderCell: params => <AttributeCell value={params.value} />,
				minWidth: 120,
				flex: 1,
				sortable: true,
				filterable: true,
				type: 'number',
				filterOperators: numericOps(['=', '>', '>=', '<', '<=']),
			},
		);
	}

	columns.push(
		{
			field: 'GRADE',
			align: 'left',
			headerName: t('Grade'),
			renderCell: params =>
				isRedesign ? (
					<GradeBadgeCell grade={params.value as string} />
				) : (
					<StatusCell status={params.value as string} />
				),
			minWidth: 150,
			width: 150,
			sortable: true,
			filterable: true,
			type: 'singleSelect',
			valueOptions: [
				{
					label: t('Optimal'),
					value: '0',
				},
				{
					label: t('Stable'),
					value: '1',
				},
				{
					label: t('Moderate'),
					value: '2',
				},
				{
					label: t('Concerning'),
					value: '3',
				},
				{
					label: t('Critical'),
					value: '4',
				},
			],
			filterOperators: getGridSingleSelectOperators().filter(
				(operator: GridFilterOperator) => {
					return (
						operator.value == 'is' || operator.value == 'isAnyOf'
					);
				},
			),
		},
		{
			field: 'Menu',
			headerName: '',
			sortable: false,
			filterable: false,
			hideable: false,
			disableColumnMenu: true,
			width: 68,
			maxWidth: 68,
			minWidth: 68,
			renderCell: params => (
				<MenuCell
					assetId={params.value as string}
					assetType={riskConfig.riskSetting.assetType}
				/>
			),
		},
	);

	if (showDriverColumns) {
		if (weightedScores?.lofScores?.length) {
			columns.push(...weightedScores.lofScores);
		}
		if (weightedScores?.cofScores?.length) {
			columns.push(...weightedScores.cofScores);
		}
	}

	return isRedesign ? columns.map(withRedesignWidth) : columns;
}
